import React from 'react'
import { mixpanel, MixpanelProvider } from './src/hooks/useMixpanel'
import {useClickId} from "./src/hooks/useClickId";

/**
 * Utils
 */
const sanitizeHref = path => path.toLowerCase()
// Remove last trailing slash to improve consistency
const sanitizePath = path => path.trim().toLowerCase().replace(/\/$/, '')
// Alphabetically sort params
const sanitizeSearch = searchParams => {
  searchParams.sort()

  return Array
    .from(searchParams.entries())
    .map(([key, value]) => {
      return `${key}=${value}`
    })
}

const trackPageView = async (location, searchParams) => {
  await Promise.all([
    mixpanel.track('website:page_viewed', {
      pageLocation: sanitizeHref(location.origin + location.pathname),
      pagePath: sanitizePath(location.pathname),
      pageSearch: sanitizeSearch(searchParams),
      pageTitle: document.title
    }),
    fetch('https://api.tate.cloud/view/website')
  ])
}

/**
 * Loads Mixpanel and setup an unload handler
 */
export const onClientEntry = () => {
  console.log('onClientEntry')
  mixpanel.init(process.env.MIXPANEL_API_KEY, {
    batch_requests: false,
    debug: true,
    ignore_dnt: true,
    track_pageview: false
  })

  window.addEventListener('unload', function () { mixpanel.set_config({ api_transport: 'sendBeacon' }) })
}

/**
 * Load third-party scripts
 */
export const onInitialClientRender = () => {
  console.log('onInitialClientRender')
  const scriptElement = document.createElement('script')
  scriptElement.type = 'text/javascript'
  scriptElement.src = '/scripts/third-parties.js'
  document.head.appendChild(scriptElement)

  // TODO: Assign random email to user
}

/**
 * Track pageviews
 */
export const onRouteUpdate = ({ location }) => {
  console.log('onRouteUpdate')
  const decodedSearch = decodeURI(location.search)
  const searchParams = new URLSearchParams(decodedSearch)

  if ('requestAnimationFrame' in window) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => trackPageView(location, searchParams))
    })
  } else {
    // simulate 2 rAF calls
    setTimeout(() => trackPageView(location, searchParams), 32)
  }

  // UTM support
  const payload = {
    utm_campaign: searchParams.get('utm_campaign') || undefined,
    utm_content: searchParams.get('utm_content') || undefined,
    utm_medium: searchParams.get('utm_medium') || undefined,
    utm_source: searchParams.get('utm_source') || undefined,
    utm_term: searchParams.get('utm_term') || undefined
  }
  const lastUTM = {}
  if (payload.utm_campaign) lastUTM.last_utm_campaign = payload.utm_campaign
  if (payload.utm_content) lastUTM.last_utm_content = payload.utm_content
  if (payload.utm_medium) lastUTM.last_utm_medium = payload.utm_medium
  if (payload.utm_source) lastUTM.last_utm_source = payload.utm_source
  if (payload.utm_term) lastUTM.last_utm_term = payload.utm_term
  mixpanel.people.set(lastUTM)

  const firstUTM = {}
  if (payload.utm_campaign) firstUTM.first_utm_campaign = payload.utm_campaign
  if (payload.utm_content) firstUTM.first_utm_content = payload.utm_content
  if (payload.utm_medium) firstUTM.first_utm_medium = payload.utm_medium
  if (payload.utm_source) firstUTM.first_utm_source = payload.utm_source
  if (payload.utm_term) firstUTM.first_utm_term = payload.utm_term
  mixpanel.people.set_once(firstUTM)
}

const ClickIdManager = ({children}) => {
  useClickId()
  return children
}

/**
 * Mixpanel Decorator
 */
export const wrapRootElement = ({ element }) => (
  <MixpanelProvider>
      {element}
  </MixpanelProvider>
)

export const wrapPageElement = ({element}) => (
  <ClickIdManager>
    {element}
  </ClickIdManager>
)
